import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useStringsContext } from '../../../../contexts/strings';
import { usePollNotificationConf } from '../../../../hooks/usePolls';
import { useSegments } from '../../../../hooks/useSegments';
import moment from 'moment';
import { PollListDataGridFormat } from '../hooks/usePollsList';
import PushNotificationPopup, {
  PushNotificationFormPayload,
} from '../../../common/push-notification/push-notification-form';
import PushNotificationColumn from '../../../common/push-notification/push-notification-column';
import { pollTypeString } from '../constants';
import { PollType } from '../types';

interface Props {
  poll: PollListDataGridFormat;
  selectedAbbrv: string;
}

export default function PollPushNotificationColumn(props: Props) {
  const { strings } = useStringsContext();
  const { selectedAbbrv, poll } = props;
  const [openPushNotificationPopup, setOpenPushNotificationPopup] = React.useState(false);
  const { query, createMutation, updateMutation, deleteMutation } = usePollNotificationConf({
    abbrv: selectedAbbrv,
    id: poll.id,
  });
  const { query: segmentQuery } = useSegments(selectedAbbrv);
  const isLoading = query.isLoading || segmentQuery.isLoading;
  const pollNotificationConf = query.data;
  const pushNotificationId = pollNotificationConf?.id;

  if (isLoading) {
    return <>{strings.pushNotifications.loading}</>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <PushNotificationColumn
        pushNotification={pollNotificationConf}
        onClick={() => {
          setOpenPushNotificationPopup(true);
        }}
        abbrv={selectedAbbrv}
      />

      {openPushNotificationPopup ? (
        <PushNotificationPopup
          abbrv={selectedAbbrv}
          itemInfo={
            <Alert icon={false} severity="info" color="info" variant="standard">
              <Typography variant="h6">
                {poll.title} - {pollTypeString[poll.type as PollType]}
              </Typography>
              {poll.startTime ? moment(poll.startTime).format('YYYY-MM-DD HH:mm') : ''}
            </Alert>
          }
          content={{
            target: pollNotificationConf?.notification?.target,
            scheduledTime: pollNotificationConf?.scheduledTime
              ? moment(pollNotificationConf.scheduledTime).format('YYYY-MM-DD[T]HH:mm')
              : poll.startTime && moment(poll.startTime).isSameOrAfter(moment().add(1, 'hour'))
                ? moment(poll.startTime).format('YYYY-MM-DD[T]HH:mm')
                : undefined,
            title: pollNotificationConf?.notification?.title || poll.title || '',
            description:
              pollNotificationConf?.notification?.description ||
              pollTypeString[poll.type as PollType] ||
              '',
          }}
          segments={segmentQuery.data || []}
          pushNotificationIsSend={pollNotificationConf?.status === 'SENT'}
          onCreate={async (payload: PushNotificationFormPayload) => {
            const { scheduledTime, target, title, description } = payload;
            const res = await createMutation.mutateAsync({
              scheduledTime,
              notification: {
                title,
                description,
                pollId: poll.id,
                target,
              },
            });
            query.refetch();
            return res;
          }}
          onUpdate={(payload: PushNotificationFormPayload) => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const { scheduledTime, target, title, description } = payload;
            return updateMutation.mutateAsync({
              id: pushNotificationId,
              scheduledTime,
              notification: {
                title,
                description,
                pollId: poll.id,
                target,
              },
              createdAt: (pollNotificationConf?.createdAt || moment().format()) as string,
            });
          }}
          onDelete={async () => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const res = await deleteMutation.mutateAsync({
              id: pushNotificationId,
              pollId: poll.id,
            });
            query.refetch();
            return res;
          }}
          onClose={() => {
            setOpenPushNotificationPopup(false);
          }}
          isPending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            query.isLoading
          }
          isNew={!pushNotificationId}
        />
      ) : null}
    </Box>
  );
}
